import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { apiFetch } from "../lib/api.js";
import { IllustrationCompass, IllustrationBands } from "../components/illustrations.jsx";

export default function Diagnostic() {
  const [, navigate] = useLocation();
  const [content, setContent] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [step, setStep] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    apiFetch("/content/diagnostic").then((c) => {
      setContent(c);
      setAnswers(new Array(c.questions.length).fill(null));
    });
  }, []);

  function selectAnswer(value) {
    setAnswers((prev) => {
      const next = [...prev];
      next[value.step] = value.answer;
      return next;
    });
  }

  useEffect(() => {
    function onKeyDown(e) {
      if (result || !content) return;
      const num = Number(e.key);
      if (num >= 1 && num <= content.scale.length) {
        selectAnswer({ step, answer: num - 1 });
        if (step < content.questions.length - 1) {
          setTimeout(() => setStep((s) => s + 1), 150);
        }
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  });

  if (!content) return null;

  async function takeToCoach() {
    setStarting(true);
    try {
      const session = await apiFetch("/sessions", { method: "POST", body: { type: "first" } });
      if (session.starterMessage) {
        sessionStorage.setItem(`coach-starter-${session.id}`, session.starterMessage);
      }
      navigate(`/coach/${session.id}`);
    } catch (e) {
      setError(e.message);
      setStarting(false);
    }
  }

  if (result) {
    const weakest = [...result.domainScores]
      .sort((a, b) => a.percent - b.percent)
      .slice(0, 2)
      .map((d) => d.domain);

    return (
      <div className="page">
        <div className="kicker" style={{ marginBottom: 12 }}>
          <span className="the-line" />
          THE STANDING CHECK
        </div>
        <div className="illustration-hero-wrap">
          <IllustrationBands className="illustration-hero" />
        </div>
        <h1 style={{ marginBottom: 8 }}>
          <span className={`band-label band-${result.overallBand}`}>{result.overallBand}</span>
        </h1>
        <div className="display" style={{ marginBottom: 24 }}>{result.overallPercent}%</div>
        <p style={{ marginBottom: 32, color: "var(--muted)" }}>{content.overallCopy[result.overallBand]}</p>

        <div className="card" style={{ marginBottom: 32 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {result.domainScores.map((d) => (
              <div key={d.domain}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span>{d.domain}</span>
                  <span className={`band-label band-${d.band}`}>{d.band} - {d.percent}%</span>
                </div>
                <div className="meter">
                  <div className={`meter-fill band-${d.band}`} style={{ width: `${d.percent}%` }} />
                </div>
                {content.stateCopy && content.stateCopy[d.band] && (
                  <p style={{ marginTop: 10, fontSize: 14 }}>{content.stateCopy[d.band]}</p>
                )}
                {weakest.includes(d.domain) && (
                  <p style={{ marginTop: 10, color: "var(--muted)", fontSize: 14 }}>{content.insights[d.domain]}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        <button className="btn-primary" onClick={takeToCoach} disabled={starting}>
          Claim your ground
        </button>
      </div>
    );
  }

  const question = content.questions[step];
  const progress = ((step) / content.questions.length) * 100;

  async function submit() {
    setError(null);
    try {
      const row = await apiFetch("/diagnostic", { method: "POST", body: { answers } });
      setResult(row);
    } catch (e) {
      setError(e.message);
    }
  }

  const allAnswered = answers.every((a) => a !== null);

  return (
    <div className="page">
      <div className="kicker" style={{ marginBottom: 12 }}>
        <span className="the-line" />
        THE STANDING CHECK
      </div>
      <div className="progress-bar" style={{ marginBottom: 32 }}>
        <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
      </div>

      {step === 0 && (
        <div className="illustration-hero-wrap">
          <IllustrationCompass className="illustration-empty" />
        </div>
      )}

      <div style={{ color: "var(--muted)", fontSize: 13, marginBottom: 12 }}>
        {content.domains[question.domain]} · Question {step + 1} of {content.questions.length}
      </div>
      <h1 style={{ marginBottom: 8, fontSize: 24 }}>{question.text}</h1>

      <div className="choice-list">
        {content.scale.map((label, i) => (
          <button
            key={i}
            className={answers[step] === i ? "selected" : ""}
            onClick={() => {
              selectAnswer({ step, answer: i });
              if (step < content.questions.length - 1) {
                setTimeout(() => setStep((s) => s + 1), 150);
              }
            }}
          >
            {label}
          </button>
        ))}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 32 }}>
        <button className="btn-secondary" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}>
          Back
        </button>
        {step === content.questions.length - 1 && (
          <button className="btn-primary" onClick={submit} disabled={!allAnswered}>
            See where I stand
          </button>
        )}
      </div>
      {error && <p style={{ color: "var(--error)", marginTop: 16 }}>{error}</p>}
    </div>
  );
}
